// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@/constants/hardfin-standard-icons';
import { CostDataUploadResult } from '@/costs/api/upload-cost-data';
import type { ExpandedCostsState } from '@/costs/components/cost-data-upload/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: CostDataUploadResult | null;
  expandedCosts: ExpandedCostsState;
  onToggleExpanded: (section: keyof ExpandedCostsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  description: string;
  errors: string[];
}

type CostItem = CostDataUploadResult['items'][number];

interface CostRowProps {
  index: number;
  style: React.CSSProperties;
  data: CostItem[];
}

const CostRow = ({ index, style, data }: CostRowProps) => {
  const cost = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Typography variant="body2">
              <strong>{cost.description || 'Cost entry'}</strong> for{' '}
              <strong>
                {formatCurrencyValue(cost.amount, { placeholder: 'Not set' })}
              </strong>{' '}
              on <strong>{cost.date}</strong>
            </Typography>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.description ? ` (cost "${row.description}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
        />
      </ListItem>
    </div>
  );
};

export function Results(props: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;

  const costsCreated = items.filter(
    (item) => !item.errors || item.errors.length === 0
  );
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking copy
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'description', 'cost')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  // Process row errors (validation errors)
  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        description: '',
        errors: row.errors.map((err) => `${err.fieldName}: ${err.error}`),
      });
    }
  });

  // Process item errors (individual cost errors)
  items.forEach((item) => {
    if (item.errors && item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        description: item.description || '',
        errors: item.errors.map((err) => `${err.fieldName}: ${err.error}`),
      });
    }
  });

  const totalProcessed = costsCreated.length;
  const hasErrors = failedRows.length > 0 || structureErrors.length > 0;

  return (
    <Card>
      <CardContent>
        {success && !hasErrors ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your cost data uploaded successfully for{' '}
                {pluralize(totalProcessed, {
                  singular: 'cost',
                  plural: 'costs',
                })}
              </Typography>
            </Alert>

            {costsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: costsCreated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    costsCreated.length
                      ? () => props.onToggleExpanded('created')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Costs created ({formatNumber(costsCreated.length)})
                    </Typography>
                  </Box>
                  {costsCreated.length > 0 &&
                    (props.expandedCosts.created ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedCosts.created}>
                  <VirtualList
                    height={Math.min(costsCreated.length * ITEM_HEIGHT, 200)}
                    itemCount={costsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={costsCreated}
                    width="100%"
                  >
                    {CostRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required cost data fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match required
                    fields
                  </Typography>
                </>
              )}
            </Alert>

            {/* Show costs created or valid rows */}
            {costsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('created')}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      {hasErrors
                        ? `Valid rows (${formatNumber(costsCreated.length)})`
                        : `Costs created (${formatNumber(costsCreated.length)})`}
                    </Typography>
                  </Box>
                  {props.expandedCosts.created ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedCosts.created}>
                  <VirtualList
                    height={Math.min(costsCreated.length * ITEM_HEIGHT, 200)}
                    itemCount={costsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={costsCreated}
                    width="100%"
                  >
                    {CostRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {/* Show structure errors */}
            {structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {props.expandedCosts.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedCosts.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{`${row.fieldName}`}" not found
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}

            {/* Show failed rows */}
            {failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'cost',
                        plural: 'costs',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedCosts.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedCosts.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
